import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Eye, Bell,
  ShieldCheck, Clock, XCircle
} from 'lucide-react';
import TierBadge from '../TierBadge';
import AnimatedCounter from './AnimatedCounter';
import OpportunityTicker from './OpportunityTicker';
import NewsCarousel from './NewsCarousel';
import AnnouncementsPanel from './AnnouncementsPanel';
import ActivityFeed from './ActivityFeed';
import InviteCard from './InviteCard';
import OpportunitiesList from './OpportunitiesList';
import CommodityTracker from './CommodityTracker';
import TasksWidget from './TasksWidget';

const SellerDashboard = ({
  stats,
  tierStatus,
  config,
  tickerDeals,
  activities,
  articles,
  announcements,
  myDeals,
  moduleOrder,
}) => {
  const navigate = useNavigate();

  const approvedTier = tierStatus?.approved_tier || 'none';
  const currentTierStatus = tierStatus?.tier_status || 'not_started';
  const pendingTier = tierStatus?.tier || 'none';
  const tierApprovedAt = approvedTier !== 'none' ? tierStatus?.[`${approvedTier}_approved_at`] : null;
  const tierDate = tierApprovedAt
    ? new Date(tierApprovedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const needsBronze = approvedTier === 'none' && currentTierStatus !== 'pending';
  const nextTier = approvedTier === 'none' ? 'bronze' : approvedTier === 'bronze' ? 'silver' : approvedTier === 'silver' ? 'gold' : null;

  const mod = (key) => config?.[key]?.enabled !== false;

  // Render a module by key
  const renderModule = (key) => {
    switch (key) {
      case 'animated_counters':
        return null; // Rendered in the fixed tier row
      case 'latest_opportunities':
        if (!mod('latest_opportunities')) return null;
        return (
          <OpportunitiesList
            key={key}
            deals={myDeals}
            title="My Opportunities"
            showMyDeals={true}
            maxItems={config?.latest_opportunities?.count || 5}
          />
        );
      case 'activity_feed':
        if (!mod('activity_feed')) return null;
        return <ActivityFeed key={key} activities={activities} />;
      case 'news_articles':
        if (!mod('news_articles')) return null;
        return <NewsCarousel key={key} articles={articles} />;
      case 'announcements':
        if (!mod('announcements')) return null;
        return (
          <AnnouncementsPanel
            key={key}
            announcements={announcements}
            maxItems={config?.announcements?.count || 5}
          />
        );
      case 'commodity_tracker':
        if (!mod('commodity_tracker')) return null;
        return <CommodityTracker key={key} count={config?.commodity_tracker?.count || 6} />;
      case 'my_tasks':
        return <TasksWidget key={key} maxItems={5} />;
      case 'opportunity_ticker':
        return null; // Rendered separately at the bottom (full width)
      default:
        return null;
    }
  };

  // Build ordered list of visible modules (excluding counters and ticker)
  const orderedModules = (moduleOrder || [])
    .filter(key => key !== 'animated_counters' && key !== 'opportunity_ticker')
    .map(key => renderModule(key))
    .filter(Boolean);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Account Type Indicator */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wide">
          <Package className="w-3.5 h-3.5" />
          Seller Dashboard
        </span>
      </div>

      {/* Row 1: Tier Status + Counters (always fixed) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Tier Status Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <TierBadge
              tier={approvedTier}
              tierStatus={approvedTier !== 'none' ? 'approved' : currentTierStatus}
              size="lg"
              showLabel={false}
            />
            <div className="min-w-0">
              <p className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {approvedTier === 'none' ? 'Unverified' : `${approvedTier.charAt(0).toUpperCase() + approvedTier.slice(1)} Tier`}
              </p>
              {tierDate && <p className="text-xs sm:text-sm text-gray-500">Member since {tierDate}</p>}
              <p className="text-xs sm:text-sm text-gray-500">{stats?.account_type || 'Seller'}</p>
            </div>
          </div>

          {currentTierStatus === 'pending' && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-amber-800 text-xs">
                {pendingTier.charAt(0).toUpperCase() + pendingTier.slice(1)} verification pending review.
              </span>
            </div>
          )}

          {currentTierStatus === 'rejected' && tierStatus?.tier_rejection_reason && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-red-800 text-xs">Rejected: {tierStatus.tier_rejection_reason}</span>
            </div>
          )}

          {needsBronze && (
            <button
              onClick={() => navigate('/admin/verify/bronze')}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium py-2.5 rounded-lg transition"
            >
              <ShieldCheck className="w-4 h-4" />
              Complete Bronze Verification
            </button>
          )}
          {!needsBronze && nextTier && currentTierStatus !== 'pending' && (
            <button
              onClick={() => navigate(`/admin/verify/${nextTier}`)}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 rounded-lg transition"
            >
              Upgrade to {nextTier.charAt(0).toUpperCase() + nextTier.slice(1)}
            </button>
          )}
        </div>

        {/* Animated Counters */}
        {mod('animated_counters') && (
          <div className="lg:col-span-2 grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
            <AnimatedCounter value={stats?.active_deals || 0} label="Active Deals" icon={Package} color="blue" />
            <AnimatedCounter value={stats?.total_deals || 0} label="Total Deals" icon={Package} color="green" />
            <AnimatedCounter value={stats?.deal_views || 0} label="Total Views" icon={Eye} color="orange" />
            <AnimatedCounter value={stats?.unread_notifications || 0} label="Notifications" icon={Bell} color={stats?.unread_notifications > 0 ? 'rose' : 'purple'} />
          </div>
        )}
      </div>

      {/* Dynamic modules rendered in user-defined order */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {orderedModules}
      </div>

      {/* Invite Card (always visible, not reorderable) */}
      <InviteCard
        invitesSent={stats?.invites_sent || 0}
        invitesJoined={stats?.invites_joined || 0}
      />

      {/* Ticker (always at bottom) */}
      {mod('opportunity_ticker') && tickerDeals.length > 0 && (
        <OpportunityTicker
          deals={tickerDeals}
          speed={config?.opportunity_ticker?.speed || 'normal'}
        />
      )}
    </div>
  );
};

export default SellerDashboard;
